// contexts/voice/useVoiceUIActions.ts
// Hook for handling UI actions from the voice agent

import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../CartContext';
import {
  UIAction,
  TrackingData,
  PickupConfirmationData,
  CallbackConfirmationData,
  HandoverSummary,
  AgentInfo,
} from './types';
import { VOICE_CONSTANTS } from './constants';
import { Product } from '../../components/ProductCard';
import { getIdToken } from '../../firebase';

interface UseVoiceUIActionsProps {
  onShowTracking: (data: TrackingData) => void;
  onShowPickupConfirmation: (data: PickupConfirmationData) => void;
  onShowCallbackConfirmation: (data: CallbackConfirmationData) => void;
  onInitiateHandover: (data: { summary: HandoverSummary; newAgent: AgentInfo; reason?: string; urgency?: string }) => void;
  onChangeAgent: (agent: AgentInfo) => void;
}

export function useVoiceUIActions({
  onShowTracking,
  onShowPickupConfirmation,
  onShowCallbackConfirmation,
  onInitiateHandover,
  onChangeAgent,
}: UseVoiceUIActionsProps) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const {
    addToCart: cartAddToCart,
    removeFromCart: cartRemoveFromCart,
    updateQuantity: cartUpdateQuantity,
    clearCart: cartClearCart,
    refreshOrders: cartRefreshOrders,
  } = useCart();

  // Product highlighting state
  const [highlightedProductIds, setHighlightedProductIds] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const clearHighlights = useCallback(() => {
    setHighlightedProductIds([]);
  }, []);

  // Product image cycling - dispatches custom events for ProductModal to handle
  const nextProductImage = useCallback(() => {
    window.dispatchEvent(new CustomEvent('product-image-next'));
  }, []);

  const previousProductImage = useCallback(() => {
    window.dispatchEvent(new CustomEvent('product-image-previous'));
  }, []);

  // Main UI action handler
  const handleUIAction = useCallback(async (action: UIAction) => {
    switch (action.action) {
      case 'show_products':
        if (action.product_ids) {
          setHighlightedProductIds(action.product_ids);
          if (action.category) {
            if (window.location.pathname === '/') setSearchParams({ category: action.category });
            else navigate(`/?category=${action.category}`);
          }
          setTimeout(() => setHighlightedProductIds([]), VOICE_CONSTANTS.HIGHLIGHT_TIMEOUT_MS);
        }
        break;

      case 'open_product_popup':
        if (action.product_id) {
          try {
            const token = await getIdToken();
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const res = await fetch(`/api/products/${action.product_id}`, { headers });
            const product = await res.json();
            if (product && !product.error) {
              setSelectedProduct(product);
            }
          } catch (err) {
            console.error('Error fetching product for popup:', err);
          }
        }
        break;

      case 'close_product_popup':
        setSelectedProduct(null);
        break;

      case 'next_product_image':
        nextProductImage();
        break;

      case 'previous_product_image':
        previousProductImage();
        break;

      case 'highlight_product':
        if (action.product_id) {
          setHighlightedProductIds([action.product_id]);
          setTimeout(() => setHighlightedProductIds([]), VOICE_CONSTANTS.HIGHLIGHT_TIMEOUT_MS);
        }
        break;

      case 'compare_products':
      case 'highlight_products':
        if (action.product_ids) {
          setHighlightedProductIds(action.product_ids);
        }
        break;

      case 'clear_highlights':
        setHighlightedProductIds([]);
        break;

      case 'category':
        if (action.category) {
          if (window.location.pathname === '/') setSearchParams({ category: action.category });
          else navigate(`/?category=${action.category}`);
          setTimeout(() => {
            document.querySelector('[data-section="products"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
        break;

      case 'subcategory':
        if (action.category && action.subcategory) {
          if (window.location.pathname === '/') setSearchParams({ category: action.category, subcategory: action.subcategory });
          else navigate(`/?category=${action.category}&subcategory=${action.subcategory}`);
          setTimeout(() => {
            document.querySelector('[data-section="products"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
        break;

      case 'navigate':
        if (action.page === 'home') navigate('/');
        else if (action.page === 'cart') navigate('/winkelwagen');
        else if (action.page === 'orders') navigate('/bestellingen');
        else if (action.page === 'checkout') navigate('/afrekenen');
        else if (action.page === 'category' && action.category) {
          setSearchParams({ category: action.category });
        }
        else if (action.page === 'subcategory' && action.category && action.subcategory) {
          setSearchParams({ category: action.category, subcategory: action.subcategory });
        }
        else if (action.page === 'next_page') {
          const url = new URL(window.location.href);
          const p = parseInt(url.searchParams.get('page') || '1', 10);
          url.searchParams.set('page', (p + 1).toString());
          navigate(url.pathname + url.search);
        }
        else if (action.page === 'previous_page') {
          const url = new URL(window.location.href);
          const p = parseInt(url.searchParams.get('page') || '1', 10);
          if (p > 1) {
            if (p === 2) url.searchParams.delete('page');
            else url.searchParams.set('page', (p - 1).toString());
            navigate(url.pathname + url.search);
          }
        }
        else if (action.page === 'page' && action.page_number) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', action.page_number.toString());
          navigate(url.pathname + url.search);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;

      case 'add_to_cart':
        if (action.product_id) {
          const actionKey = `add_${action.product_id}_${Date.now() - (Date.now() % VOICE_CONSTANTS.DEBOUNCE_ADD_TO_CART_MS)}`;
          if ((window as unknown as { __lastAddAction?: string }).__lastAddAction === actionKey) break;
          (window as unknown as { __lastAddAction?: string }).__lastAddAction = actionKey;

          try {
            const token = await getIdToken();
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const res = await fetch(`/api/products/${action.product_id}`, { headers });
            const product = await res.json();
            if (product && !product.error) {
              cartAddToCart(product, action.quantity || 1);
            }
          } catch (err) {
            console.error('Error adding to cart:', err);
          }
        }
        break;

      case 'show_cart':
        navigate('/winkelwagen');
        break;

      case 'clear_cart':
        cartClearCart();
        break;

      case 'remove_from_cart':
        if (action.product_id) cartRemoveFromCart(action.product_id);
        break;

      case 'update_cart_quantity':
        if (action.product_id && action.quantity !== undefined) {
          cartUpdateQuantity(action.product_id, action.quantity);
        }
        break;

      case 'start_checkout':
        navigate('/afrekenen');
        break;

      case 'search':
        if (action.query) {
          if (window.location.pathname === '/') setSearchParams({ search: action.query });
          else navigate(`/?search=${encodeURIComponent(action.query)}`);
          setTimeout(() => {
            document.querySelector('[data-section="products"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
        break;

      case 'scroll_to':
        if (action.section) {
          if (action.section === 'top') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            const selector = action.section === 'products' ? '[data-section="products"]' :
                           action.section === 'categories' ? '[data-section="categories"]' :
                           action.section === 'footer' ? 'footer' : null;
            if (selector) {
              document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
        break;

      case 'open_product_detail':
        if (action.product_id) navigate(`/product/${action.product_id}`);
        break;

      case 'get_cart_contents':
        navigate('/winkelwagen');
        break;

      case 'show_tracking':
        if (action.order_id && action.tracking) {
          onShowTracking({
            orderId: action.order_id,
            tracking: action.tracking,
            items: action.items || []
          });
        }
        break;

      case 'show_pickup_confirmation':
        if (action.confirmation_number) {
          onShowPickupConfirmation({
            confirmationNumber: action.confirmation_number,
            date: action.date || '',
            timeSlot: action.time_slot || '',
            itemName: action.item_name || '',
            itemPrice: action.item_price,
            address: action.address || ''
          });
        }
        break;

      case 'show_callback_confirmation':
        if (action.confirmation_number) {
          onShowCallbackConfirmation({
            confirmationNumber: action.confirmation_number,
            day: action.day || '',
            date: action.date || '',
            time: action.time || '',
            reason: action.reason || ''
          });
        }
        break;

      case 'initiate_handover':
        if (action.new_agent) {
          onInitiateHandover({
            summary: action.summary || {},
            newAgent: action.new_agent,
            reason: action.reason,
            urgency: action.urgency
          });
        }
        break;

      case 'agent_changed':
        if (action.new_agent) onChangeAgent(action.new_agent);
        break;

      case 'refresh_orders':
        cartRefreshOrders();
        break;
    }
  }, [
    navigate,
    setSearchParams,
    cartAddToCart,
    cartRemoveFromCart,
    cartUpdateQuantity,
    cartClearCart,
    cartRefreshOrders,
    onShowTracking,
    onShowPickupConfirmation,
    onShowCallbackConfirmation,
    onInitiateHandover,
    onChangeAgent,
    nextProductImage,
  ]);

  return {
    // State
    highlightedProductIds,
    selectedProduct,

    // Actions
    handleUIAction,
    clearHighlights,
    setSelectedProduct,
    nextProductImage,
    previousProductImage,
  };
}
